import { useState, useEffect, useRef } from 'react'
import { createUseStyles, useTheme } from 'react-jss'
import style from './style'
import {
  InputIcon,
  Icons
} from 'UI'
import Text from './text.json'
import { TrainerPrimaryPath, SearchPath, CourseNeutralPath, LearningPathNeutralPath } from 'navigation/Routes'
import SearchItem from './SearchItem'
import { useSelector } from 'react-redux'
import { useRouteMatch } from 'react-router-dom'
import { ProfileTypes, showLearningPath } from 'constants/'

const useStyle = createUseStyles(style)

const keyCodeEnter = 13
const keyCodeEscape = 27
const showTagsOptionsInSearch = false

export default ({ Tags, Courses, Authors, LearningPaths, goToLink, searchTerm, setSearchTerm, profileType, setSearchPageData, searchTermsHistory, topBarOpen, bannerOpen }) => {
  const theme = useTheme()
  const node = useRef()
  const [openSearchResults, setOpenSearchResults] = useState(false)
  const [focused, setFocused] = useState(false)
  const focusedRef = useRef(focused)
  const searchTermForUrl = useSelector(({ search }) => search.searchTerm)
  const searchTermRef = useRef(searchTermForUrl)
  const handleChangeCustom = e => {
    setOpenSearchResults(true)
    const value = e.target.value
    const isSearchPage = matchSearchPage !== null
    setSearchTerm(value, isSearchPage)
    searchTermRef.current = value
    return setSearchTerm
  }

  const matchSearchPage = useRouteMatch(SearchPath)

  const setFocusedWithRef = isFocused => {
    focusedRef.current = isFocused
    setFocused(isFocused)
  }

  const searchAction = () => {
    setSearchPageData()
    setOpenSearchResults(false)
    goToLink(`${SearchPath}?query=${encodeURIComponent(searchTermRef.current)}`)
  }

  const escAndEnterFunction = ({ keyCode }) => {
    keyCode === keyCodeEscape && setOpenSearchResults(false)
    if (focusedRef.current && keyCode === keyCodeEnter) searchAction()
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleClick)
    document.addEventListener('keydown', escAndEnterFunction, false)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', escAndEnterFunction, false)
      setSearchTerm('')
    }
  }, [])
  const handleClick = e => (!node.current.contains(e.target) && !openSearchResults) && setOpenSearchResults(false)
  const takeLearningPathResult = showLearningPath ? LearningPaths.length === 0 : true
  const resultIsEmpty = Tags.length === 0 && Courses.length === 0 && Authors.length === 0 && takeLearningPathResult
  const isSubscribed = profileType === ProfileTypes.BasicSubscriber || profileType === ProfileTypes.PremiumSubscriber
  const { root, inputGroup, input, controlIcon, results, searchItem, icon, textClass } = useStyle({ secondIcon: searchTerm.length, topBarOpen, bannerOpen })
  return (
    <div className={root} ref={node}>
      <div className={inputGroup}>
        <input
          id='search'
          data-test='search-input'
          className={input}
          type='text'
          value={searchTermForUrl}
          placeholder={Text.placeholder}
          onChange={handleChangeCustom}
          onFocus={() => {
            setFocusedWithRef(true)
            setOpenSearchResults(true)
          }}
          onBlur={() => setFocusedWithRef(false)}
          autoComplete='off'
        />
        <InputIcon
          cursor='pointer'
          height='100%'
          color={theme.primary[0]}
          iconName={Icons.search}
          handleClick={searchAction}
          className={controlIcon}
        />
      </div>
      {
        openSearchResults &&
        (
          <div className={results}>
            {
              showTagsOptionsInSearch
                ? Tags.map((tag, key) =>
                  <SearchItem
                    onClick={() => {
                      goToLink(`${SearchPath}?query=${encodeURIComponent(tag.TagOriginal)}`)
                      setOpenSearchResults(false)
                    }}
                    type='Tags'
                    searchItem={searchItem}
                    icon={icon}
                    content={tag.HighlightedTag}
                    key={key}
                    textClass={textClass}
                  />
                  )
                : null
            }
            {
              showLearningPath
                ? (LearningPaths.map(path =>
                  <SearchItem
                    onClick={() => {
                      goToLink(`${LearningPathNeutralPath}/${path.Slug}`)
                      setOpenSearchResults(false)
                    }}
                    type='learningPaths'
                    searchItem={searchItem}
                    icon={icon}
                    content={path.Highlight}
                    key={path.Id}
                    textClass={textClass}
                  />
                  ))
                : null
            }
            {
              Courses.map(course =>
                <SearchItem
                  dataTest='search-item-course'
                  onClick={() => {
                    goToLink(`${CourseNeutralPath}/${course.Slug}`)
                    setOpenSearchResults(false)
                  }}
                  type='Courses'
                  searchItem={searchItem}
                  icon={icon}
                  content={course.Highlight}
                  key={course.CourseId}
                  textClass={textClass}
                  isFree={course.IsFree && !isSubscribed}
                />
              )
            }
            {
              Authors.map(author =>
                <SearchItem
                  onClick={() => {
                    goToLink(`${TrainerPrimaryPath}/${author.Slug}`)
                    setOpenSearchResults(false)
                  }}
                  type='Authors'
                  searchItem={searchItem}
                  icon={icon}
                  content={author.HighlightedText || author.Text}
                  key={author.AuthorId}
                  textClass={textClass}
                />
              )
            }
            {
              resultIsEmpty && searchTermsHistory.length > 0
                ? searchTermsHistory.map(term =>
                  <SearchItem
                    onClick={() => {
                      goToLink(`${SearchPath}?query=${encodeURIComponent(term)}`)
                      handleChangeCustom({ target: { value: term } })
                      setOpenSearchResults(false)
                    }}
                    type='history'
                    searchItem={searchItem}
                    icon={icon}
                    content={term}
                    key={term}
                    textClass={textClass}
                  />
                  )
                : null
            }
            {
              resultIsEmpty && searchTermsHistory.length === 0
                ? <SearchItem
                    notFound
                    searchItem={searchItem}
                    content={Text.notFound}
                    textClass={textClass}
                  />
                : null
            }
          </div>
        )
      }
    </div>
  )
}
